import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, BarChart3, CalendarRange, Handshake, Lock, Megaphone, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const FEATURES = [
    {
        id: "ai",
        title: "Ask your business anything",
        heading: "AI & Business Intelligence.",
        body: "Ask Ted answers questions in plain English using your live operational data — jobs, sales, stock, people and money. Risks and trends surface before they cost you, so decisions stop depending on gut feel.",
        cta: "Explore AI & Intelligence",
        shotLabel: "TedOps · Ask Ted",
        rows: [
            "“Which jobs are at risk this week?”",
            "“Revenue vs last month, by depot”",
            "“Which customers haven't rebooked?”",
        ],
        icon: Sparkles,
    },
    {
        id: "sales",
        title: "Win and manage every customer",
        heading: "Sales & CRM.",
        body: "Enquiries, customer records, quotations, orders and sales activity live on one record per customer — nothing gets lost between the inbox, the spreadsheet and someone's notebook.",
        cta: "Explore Sales & CRM",
        shotLabel: "TedOps · Sales pipeline",
        rows: [
            "New enquiry — Greenway School",
            "Quote sent — service contract",
            "Order confirmed — BO-1132",
        ],
        icon: Handshake,
    },
    {
        id: "marketing",
        title: "Turn customer data into campaigns",
        heading: "Marketing Automation.",
        body: "Customer records, purchase history and enquiry activity become targeted email campaigns, smart segments and automated customer journeys — marketing that runs on data you already have.",
        cta: "Explore Marketing Automation",
        shotLabel: "TedOps · Campaigns",
        rows: [
            "Segment: due for service — 214 customers",
            "Win-back journey — running",
            "Service reminders — scheduled",
        ],
        icon: Megaphone,
    },
    {
        id: "operations",
        title: "Run jobs and field teams",
        heading: "Operations & Field Teams.",
        body: "The job pool, scheduling, crews, live tracking and the offline-capable field app keep office and engineers in perfect sync — from first enquiry to signed-off completion.",
        cta: "Explore Operations",
        shotLabel: "TedOps · Live schedule",
        rows: [
            "12 jobs scheduled today",
            "5 engineers en route",
            "BO-1087 completed — 10:24",
        ],
        icon: CalendarRange,
    },
    {
        id: "finance",
        title: "See the money clearly",
        heading: "Finance & Reporting.",
        body: "Invoices, bills, expenses and sales flow into a live management P&L — profitability by job, customer or depot is always visible, without building a single spreadsheet.",
        cta: "Explore Finance & Reporting",
        shotLabel: "TedOps · Management P&L",
        rows: [
            "Invoices raised this week — 38",
            "Bills awaiting approval — 6",
            "Gross margin by depot — live",
        ],
        icon: BarChart3,
    },
];

const Shot = ({ feature }) => {
    const Icon = feature.icon;
    return (
        <div className="flex h-full w-full flex-col bg-gradient-to-br from-[#F0F9FF] via-white to-[#FAF5FF] p-5">
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#0066FF]/15 bg-white shadow-sm">
                    <Icon className="h-5 w-5 text-[#0066FF]" strokeWidth={1.75} />
                </div>
                <p className="font-mono-brand text-[9px] uppercase tracking-[0.25em] text-[#94A3B8]">
                    {feature.shotLabel}
                </p>
            </div>
            <div className="mt-4 flex-1 space-y-2">
                {feature.rows.map((r) => (
                    <div
                        key={r}
                        className="flex items-center gap-2.5 rounded-lg border border-[#0F172A]/[0.05] bg-white px-3 py-2.5 shadow-sm"
                    >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#A855F7]" />
                        <p className="truncate text-xs font-semibold text-[#334155]">{r}</p>
                    </div>
                ))}
            </div>
            <p className="mt-3 text-center font-mono-brand text-[8px] uppercase tracking-[0.25em] text-[#94A3B8]">
                Concept visual — sample data
            </p>
        </div>
    );
};

const BrowserPreview = ({ feature }) => (
    <div className="relative" data-testid="screenshot-wrap">
        <div aria-hidden="true" className="absolute -inset-8 rounded-full border border-[#0F172A]/[0.07] lg:-inset-14" />
        <div aria-hidden="true" className="absolute -inset-16 hidden rounded-full border border-[#0F172A]/[0.05] lg:-inset-24 lg:block" />
        <div
            className="relative overflow-hidden rounded-2xl border border-[#0F172A]/10 bg-white shadow-[0_24px_60px_-24px_rgba(11,18,32,0.28)]"
            data-testid="screenshot-frame"
        >
            <div className="flex items-center gap-3 border-b border-[#0F172A]/[0.06] bg-[#F8FAFF] px-4 py-3">
                <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]/80" />
                </div>
                <div className="mx-auto flex items-center gap-1.5 rounded-md border border-[#0F172A]/[0.06] bg-white px-3 py-1 text-[11px] text-[#64748B]">
                    <Lock className="h-2.5 w-2.5" />
                    hts.tedopsai.com
                </div>
                <span className="w-10" aria-hidden="true" />
            </div>
            <div className="relative aspect-[16/10] w-full" data-testid="screenshot-panel">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={feature.id}
                        initial={{ opacity: 0, scale: 0.985 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.005 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="absolute inset-0"
                    >
                        <Shot feature={feature} />
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    </div>
);

const FeatureItem = ({ feature, active, onSelect, index }) => (
    <div className="relative pl-7 sm:pl-9" data-testid={`feature-item-${index + 1}`}>
        <span
            aria-hidden="true"
            className={`absolute left-0 top-1.5 h-[calc(100%-12px)] w-[2px] rounded-full transition-colors duration-300 ${
                active
                    ? "bg-gradient-to-b from-[#00E5FF] via-[#0066FF] to-[#D946EF]"
                    : "bg-[#0F172A]/10"
            }`}
        />
        <button
            type="button"
            onClick={onSelect}
            aria-expanded={active}
            aria-controls={`feature-panel-${feature.id}`}
            className="w-full rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-[#0066FF]/40"
            data-testid={`feature-button-${index + 1}`}
        >
            <AnimatePresence mode="wait" initial={false}>
                <motion.span
                    key={active ? "heading" : "title"}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    className={`block font-display text-lg font-bold leading-snug transition-colors duration-300 sm:text-xl ${
                        active ? "text-[#0F172A]" : "text-[#64748B] hover:text-[#334155]"
                    }`}
                >
                    {active ? feature.heading : feature.title}
                </motion.span>
            </AnimatePresence>
        </button>
        <AnimatePresence initial={false}>
            {active && (
                <motion.div
                    id={`feature-panel-${feature.id}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                >
                    <p className="pt-4 text-[15px] leading-relaxed text-[#475569]">{feature.body}</p>
                    <span
                        className="mt-4 inline-flex cursor-pointer items-center gap-2 text-sm font-semibold"
                        data-testid="feature-cta"
                    >
                        <span className="text-brand-gradient">{feature.cta}</span>
                        <ArrowRight className="h-4 w-4 text-[#A855F7]" />
                    </span>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

export default function WhatIsTedOps() {
    const [active, setActive] = useState(0);
    const feature = FEATURES[active];

    return (
        <section
            className="relative bg-white"
            style={{
                backgroundImage:
                    "linear-gradient(to right, rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.04) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
            }}
            data-testid="whatis-section"
            aria-labelledby="whatis-heading"
        >
            <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mx-auto max-w-3xl text-center"
                >
                    <span
                        className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 py-1.5 shadow-sm"
                        data-testid="whatis-eyebrow"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[#0066FF]" />
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#475569] sm:text-xs">
                            What is TedOps AI
                        </span>
                    </span>
                    <h2
                        id="whatis-heading"
                        className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#0F172A] sm:text-5xl"
                        data-testid="whatis-heading"
                    >
                        One Connected Platform.
                        <br className="hidden sm:block" />{" "}
                        <span className="text-brand-gradient">Powered by AI.</span>
                    </h2>
                    <p
                        className="mt-6 text-base leading-relaxed text-[#64748B] sm:text-lg"
                        data-testid="whatis-intro"
                    >
                        TedOps AI is the intelligent operations platform that connects sales,
                        marketing, customers, operations, field teams, stock and finance in one
                        place — with AI working across every workflow to surface insights, suggest
                        next actions and strip out admin.
                    </p>
                </motion.div>

                <div className="mt-16 grid items-center gap-14 lg:mt-20 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="flex flex-col gap-8 lg:gap-9"
                        data-testid="feature-list"
                    >
                        {FEATURES.map((f, i) => (
                            <FeatureItem
                                key={f.id}
                                feature={f}
                                index={i}
                                active={active === i}
                                onSelect={() => setActive(i)}
                            />
                        ))}
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
                    >
                        <BrowserPreview feature={feature} />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
